import { motion } from 'framer-motion';
import { Shield, Lock, Server, Cloud, ArrowRight } from 'lucide-react';

export function PrivacyArchitecture() {
  return (
    <section className="py-24 bg-surface-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success-500/10 border border-success-500/20 text-success-400 text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Privacy-First Architecture
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
              Local RAG.<br />
              <span className="text-success-400">Zero Retention.</span>
            </h2>

            <p className="text-lg text-text-secondary mb-8 leading-relaxed">
              Your codebase is indexed locally. Only relevant snippets are sent to the LLM,
              and nothing is stored. Enterprise-grade privacy without sacrificing capability.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-success-500/10">
                  <Lock className="w-4 h-4 text-success-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">Snippets Only</h4>
                  <p className="text-sm text-text-secondary">Only the relevant code context is sent, never your full repository</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-success-500/10">
                  <Server className="w-4 h-4 text-success-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">Local Embeddings</h4>
                  <p className="text-sm text-text-secondary">Vector search runs on your machine, not in the cloud</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-success-500/10">
                  <Shield className="w-4 h-4 text-success-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary">Audit Ready</h4>
                  <p className="text-sm text-text-secondary">Full logs of what was sent and when, for SOC2 compliance</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Architecture Diagram */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-surface-900 rounded-xl border border-surface-600 p-8">
              {/* Your Machine */}
              <div className="relative mb-8">
                <div className="bg-surface-800 rounded-lg border-2 border-dashed border-success-500/30 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Server className="w-5 h-5 text-success-400" />
                    <span className="font-semibold text-text-primary">Your Machine</span>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-surface-900 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">📁</div>
                      <span className="text-xs text-text-secondary">Codebase</span>
                    </div>
                    <div className="bg-surface-900 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">🔍</div>
                      <span className="text-xs text-text-secondary">Vector Store</span>
                    </div>
                    <div className="bg-surface-900 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">📋</div>
                      <span className="text-xs text-text-secondary">Context</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrow with label */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center gap-3 px-4 py-2 bg-primary-500/10 rounded-full border border-primary-500/20">
                  <span className="text-sm text-primary-400">snippets only</span>
                  <ArrowRight className="w-4 h-4 text-primary-400" />
                </div>
              </div>

              {/* LLM Provider */}
              <div className="relative">
                <div className="bg-surface-800 rounded-lg border border-surface-600 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Cloud className="w-5 h-5 text-text-tertiary" />
                    <span className="font-semibold text-text-primary">LLM Provider</span>
                    <span className="ml-auto text-xs text-text-tertiary bg-surface-700 px-2 py-1 rounded">Zero retention API</span>
                  </div>

                  <div className="flex gap-3">
                    <div className="bg-surface-900 rounded-lg px-3 py-2 text-xs text-text-secondary">Claude</div>
                    <div className="bg-surface-900 rounded-lg px-3 py-2 text-xs text-text-secondary">GPT-4</div>
                    <div className="bg-surface-900 rounded-lg px-3 py-2 text-xs text-text-secondary">Gemini</div>
                  </div>
                </div>
              </div>

              {/* Security badge */}
              <div className="absolute -top-3 -right-3">
                <div className="bg-success-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                  SOC2 Ready
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
